/* SpawnAnything — "Welt-Optionen": one window with a card per world mechanic (user choice: own window, see
   labs/ui-entwurf.html). The features keep their own controls, state and setup fields (setupExtras in main.js: the
   host's choice reaches every multiplayer client); this module moves those controls into the cards and adds the
   switches that only live here: bullet time, director camera at battle start and a dice for the terrain.
   Later switches (AI general, commentator, traps, physics toys) get a card via addCard(). */
import './worldOptions.css';
import { getSlowmo } from './slowmo.js';
import { createWindow } from '../ui/window.js';

const REGIE_KEY = 'aw-regie-auto';
const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

export function createWorldOptions() {
  const panel = document.getElementById('panel'); if (!panel) return null;

  /* ---- window (src/ui/window.js) ---- */
  const W = createWindow({ id: 'worldWin', title: 'Welt-Optionen', sub: 'Was in dieser Schlacht gilt', icon: 'ti-world',
    foot: 'Im Mehrspieler bestimmt der Gastgeber.' });
  const cards = document.createElement('div'); cards.className = 'wo-cards'; W.body.appendChild(cards);
  const open = () => W.open(), close = () => W.close();
  W.onOpen(() => refresh()); W.onClose(() => refresh());
  W.el.addEventListener('click', () => setTimeout(refresh, 0));
  W.el.addEventListener('change', () => setTimeout(refresh, 0));

  /** a card: icon (tabler class), title, text; body = element(s) moved in, or built by the caller */
  function addCard(icon, title, text, body, cls = '') {
    const c = document.createElement('section'); c.className = 'wo-card ' + cls;
    c.innerHTML = `<div class="wo-ct"><i class="ti ${icon}" aria-hidden="true"></i><span>${esc(title)}</span></div>${text ? `<p class="wo-txt">${esc(text)}</p>` : ''}<div class="wo-body"></div>`;
    const b = c.querySelector('.wo-body');
    for (const el of [].concat(body || [])) if (el) b.appendChild(el);
    cards.appendChild(c);
    return c;
  }
  /** a row "An | Aus" bound to get() / set(on) */
  function onOff(label, get, set) {
    const seg = document.createElement('div'); seg.className = 'seg'; seg.setAttribute('role', 'group'); seg.setAttribute('aria-label', label);
    seg.innerHTML = '<button type="button" data-on="1">An</button><button type="button" data-on="0">Aus</button>';
    const btns = seg.querySelectorAll('[data-on]');
    const sync = () => { const on = !!get(); btns.forEach(b => b.setAttribute('aria-pressed', String((b.dataset.on === '1') === on))); };
    btns.forEach(b => b.addEventListener('click', () => { set(b.dataset.on === '1'); sync(); }));
    sync(); seg._sync = sync;
    return seg;
  }

  /* ---- cards from the features' existing controls ---- */
  const terrainSel = document.getElementById('terrain');
  const terrainBlock = terrainSel?.closest('#panel > div');
  const craterRow = document.querySelector('.crater-row');
  if (terrainBlock) {
    const row = terrainSel.closest('.row');
    const dice = document.createElement('button');
    dice.className = 'ghost wo-dice'; dice.type = 'button'; dice.title = 'Zufälliges Gelände mit neuer Karte'; dice.innerHTML = '<i class="ti ti-dice-5" aria-hidden="true"></i> Würfeln';
    dice.addEventListener('click', () => {
      const opts = [...terrainSel.options].map(o => o.value).filter(v => v !== 'flach' && v !== terrainSel.value);
      const pickV = opts[(Math.random() * opts.length) | 0] || terrainSel.value;
      if (pickV !== terrainSel.value) { terrainSel.value = pickV; terrainSel.dispatchEvent(new Event('change', { bubbles: true })); }
      else document.getElementById('terrainNew')?.click(); // same preset: new map
      setTimeout(refresh, 0);
    });
    row?.appendChild(dice);
    addCard('ti-mountain', 'Gelände', 'Wähle das Schlachtfeld oder lass würfeln. „Neu“ würfelt eine neue Karte mit demselben Gelände.', row);
  }
  const wx = document.querySelector('.weather-box');
  if (wx) addCard('ti-cloud-storm', 'Wetter', '', wx);
  const zone = document.querySelector('.zone-box');
  if (zone) {
    addCard('ti-trophy', 'Siegbedingung', '', zone);
    // placing zones happens on the map: get the window out of the way
    zone.querySelector('#zonePlace')?.addEventListener('click', () => close());
  }
  if (craterRow) addCard('ti-bomb', 'Krater', 'Explosionen reißen Löcher, sprengen Hügel weg und laufen am Fluss voll Wasser.', craterRow);
  const conv = document.querySelector('.convert-box');
  if (conv) addCard('ti-biohazard', 'Infektion & Bekehrung', '', conv);
  const bt = onOff('Bullet-Time', () => { const s = getSlowmo(); return s ? s.isBulletTime() : true; }, on => { const s = getSlowmo(); if (s) s.setBulletTime(on); });
  addCard('ti-bolt', 'Bullet-Time', 'Atombombe, Sprengfass in der Menge oder der Tod eines Riesen bremsen kurz auf 0,25×.', bt);
  let regieAuto = false;
  try { regieAuto = localStorage.getItem(REGIE_KEY) === '1'; } catch (e) { /* private mode */ }
  const rg = onOff('Regie-Kamera', () => regieAuto, on => { regieAuto = on; try { localStorage.setItem(REGIE_KEY, on ? '1' : '0'); } catch (e) { /* private mode */ } });
  addCard('ti-movie', 'Regie-Kamera', 'Startet mit jeder Schlacht: die Kamera springt von selbst zu den spannendsten Stellen.', rg);
  // the AI general card stays hidden until the feature exists
  if (false) addCard('ti-chess-king', 'KI-Feldherr','Rot kämpft mit Formationen, Befehlen und Katastrophen.', (() => { const s = document.createElement('div'); s.className = 'seg'; s.innerHTML = '<button disabled>Leicht</button><button disabled>Mittel</button><button disabled>Schwer</button>'; return s; })(), 'wo-soon');
  terrainBlock?.remove(); // its controls now live in the cards

  // director camera at battle start: the battle bar (#ctrl) turning visible = a battle began
  const ctrl = document.getElementById('ctrl');
  if (ctrl) {
    let was = ctrl.hidden;
    new MutationObserver(() => {
      const now = ctrl.hidden;
      if (was && !now && regieAuto) setTimeout(() => { const s = getSlowmo(); if (s && !s.isDirecting()) s.setDirector(true); }, 400);
      was = now;
    }).observe(ctrl, { attributes: true, attributeFilter: ['hidden'] });
  }

  /* ---- graphics style: a world card too (the old panel block moves in) ---- */
  const styleBlock = document.getElementById('style')?.closest('#panel > div');
  if (styleBlock) { const c = addCard('ti-palette', 'Grafikstil', 'Licht, Farben und Stimmung der Welt. Im Kampf fest, erst nach der Runde wieder änderbar.', [...styleBlock.childNodes].filter(n => n.nodeType === 1)); cards.insertBefore(c, cards.children[1] || null); styleBlock.remove(); }

  // short summary: which mechanics are on
  let sumText = '';
  function refresh() {
    bt._sync(); rg._sync();
    const on = [];
    const stl = document.getElementById('style'); if (stl) on.push(stl.selectedOptions[0]?.textContent.trim());
    if (terrainSel) on.push(terrainSel.selectedOptions[0]?.textContent.trim());
    const w = document.getElementById('weather');
    if (w && w.selectedOptions[0]) on.push(w.selectedOptions[0].textContent.trim());
    const zm = document.querySelector('.zone-box [data-zone-mode][aria-pressed=true]');
    if (zm && zm.dataset.zoneMode !== 'vernichtung') on.push(zm.textContent.trim());
    const cr = document.querySelector('.crater-row [data-crater="1"]');
    if (cr && cr.getAttribute('aria-pressed') === 'true') on.push('Krater');
    const inf = document.getElementById('convertInf'), cv = document.getElementById('convertConv');
    if (inf && !inf.disabled && inf.getAttribute('aria-pressed') === 'true') on.push('Infektion');
    if (cv && !cv.disabled && cv.getAttribute('aria-pressed') === 'true') on.push('Bekehrung');
    const s = getSlowmo(); if (s && s.isBulletTime()) on.push('Bullet-Time');
    if (regieAuto) on.push('Regie');
    sumText = on.filter(Boolean).join(' · ');
  }
  setTimeout(refresh, 0);
  return { open, close, addCard, refresh, summary: () => { refresh(); return sumText; } };
}
